import { apiRequest } from './api'
import { ServiceProvider, ServiceType } from '../types'

interface ProvidersResponse {
  data: ServiceProvider[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export const providersService = {
  // Get all providers with optional filtering
  async getProviders(params?: {
    serviceType?: ServiceType
    city?: string
    page?: number
    limit?: number
  }): Promise<ProvidersResponse> {
    const response = await apiRequest.get<ProvidersResponse>('/providers', params)
    return response.data
  },

  // Get provider by ID
  async getProviderById(id: string): Promise<ServiceProvider> {
    const response = await apiRequest.get<ServiceProvider>(`/providers/${id}`)
    return response.data
  },

  // Get ambulance providers with location filtering
  async getAmbulanceProviders(params?: {
    lat?: number
    lng?: number
    radius?: number
  }): Promise<ServiceProvider[]> {
    const response = await apiRequest.get<ServiceProvider[]>('/providers/ambulance', params)
    return response.data
  },

  // Get provider availability
  async getProviderAvailability(providerId: string, date: string): Promise<any> {
    const response = await apiRequest.get(`/providers/${providerId}/availability`, { date })
    return response.data
  },

  // Search providers
  async searchProviders(query: string, serviceType?: ServiceType): Promise<ServiceProvider[]> {
    const params: any = { q: query }
    if (serviceType) params.serviceType = serviceType

    const response = await apiRequest.get<ServiceProvider[]>('/providers/search', params)
    return response.data
  },

  // Create provider profile
  async createProviderProfile(providerData: any): Promise<ServiceProvider> {
    const response = await apiRequest.post<ServiceProvider>('/providers', providerData)
    return response.data
  },

  // Update provider profile
  async updateProviderProfile(id: string, providerData: any): Promise<ServiceProvider> {
    const response = await apiRequest.patch<ServiceProvider>(`/providers/${id}`, providerData)
    return response.data
  },

  // Approve provider (admin only)
  async approveProvider(id: string): Promise<ServiceProvider> {
    const response = await apiRequest.patch<ServiceProvider>(`/providers/${id}/approve`)
    return response.data
  },

  // Deactivate provider (admin only)
  async deactivateProvider(id: string): Promise<ServiceProvider> {
    const response = await apiRequest.patch<ServiceProvider>(`/providers/${id}/deactivate`)
    return response.data
  },

  // Admin-specific methods
  // Get admin provider statistics
  async getAdminProviderStats(): Promise<any> {
    const response = await apiRequest.get<any>('/admin/providers/stats')
    return response.data
  },

  // Get recent providers
  async getRecentProviders(limit: number = 5): Promise<ServiceProvider[]> {
    const response = await apiRequest.get<ServiceProvider[]>('/admin/providers/recent', { limit })
    return response.data
  },

  // Get pending providers
  async getPendingProviders(): Promise<ServiceProvider[]> {
    const response = await apiRequest.get<ServiceProvider[]>('/admin/providers/pending')
    return response.data
  },

  // Get active providers with filtering
  async getActiveProviders(params?: {
    page?: number
    limit?: number
    serviceType?: string
    city?: string
    search?: string
  }): Promise<ProvidersResponse> {
    const response = await apiRequest.get<ProvidersResponse>('/admin/providers/active', params)
    return response.data
  },

  // Reject provider (admin only)
  async rejectProvider(id: string, reason: string): Promise<ServiceProvider> {
    const response = await apiRequest.patch<ServiceProvider>(`/providers/${id}/reject`, { reason })
    return response.data
  },

  // Deactivate provider with reason (admin only)
  async deactivateProviderWithReason(id: string, reason: string): Promise<ServiceProvider> {
    const response = await apiRequest.patch<ServiceProvider>(`/providers/${id}/deactivate`, { reason })
    return response.data
  },

  // Get provider analytics (admin only)
  async getProviderAnalytics(period?: string, serviceType?: string): Promise<any> {
    const params: any = {}
    if (period) params.period = period
    if (serviceType) params.serviceType = serviceType

    const response = await apiRequest.get<any>('/admin/providers/analytics', params)
    return response.data
  },
}